export type OperatorType = '==' | '!=' | '=()=' | '!=()=' | '<()=' | '<()<' | '>()=' | '>()>';

export type ValueType = 'string' | 'selection' | 'number' | 'count';

export type ActionType =
  | 'open_page'
  | 'submit'
  | 'end'
  | 'cond_show'
  | 'hide'
  | 'field_option_nothing'
  | 'field_min_value_by_reference'
  | 'matrix_target'
  | 'option_reference_by_info'
  | 'custom';

export interface ConditionItem {
  id: string;
  fieldId: string;
  targetType?: 'field' | 'additional_info';
  operator: OperatorType;
  valueType: ValueType;
  value: string;
  logicOperator?: '&&' | '||';
}

export interface ActionItem {
  type: ActionType;
  pageId?: string;
  infoMessage?: string;
  targetFieldId?: string;
  exclusiveOption?: string;
  refFieldId?: string;
  infoKey?: string;
  rawText?: string;
}

export interface RuleStatement {
  id: string;
  isConditional: boolean;
  conditions: ConditionItem[];
  action: ActionItem;
  rawStatement?: string;
}

export interface ActionRuleModel {
  preRules: RuleStatement[];
  afterRules: RuleStatement[];
}

export const generateId = (): string => Math.random().toString(36).substring(2, 9);

/**
 * Clean surrounding brackets {} and quotes "" from value string
 */
export function cleanValueString(val: string): string {
  if (!val) return '';
  let cleaned = val.trim();
  // Strip outer curly braces if present
  if (cleaned.startsWith('{') && cleaned.endsWith('}')) {
    cleaned = cleaned.slice(1, -1).trim();
  }
  // Strip outer double quotes if present
  if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
    cleaned = cleaned.slice(1, -1).trim();
  }
  // Repeat once more in case of double wrapping e.g. {"value"}
  if (cleaned.startsWith('{') && cleaned.endsWith('}')) {
    cleaned = cleaned.slice(1, -1).trim();
  }
  if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
    cleaned = cleaned.slice(1, -1).trim();
  }
  return cleaned;
}

export function parseSingleCondition(condStr: string): ConditionItem | null {
  let trimmed = condStr.trim();
  if (!trimmed) return null;

  while (trimmed.startsWith('(')) {
    trimmed = trimmed.slice(1).trim();
  }
  while (trimmed.endsWith(')')) {
    trimmed = trimmed.slice(0, -1).trim();
  }
  if (!trimmed) return null;

  // Regex to match f."field_id" or ai."info_key" operator value
  const regex = /^(f|ai)\."([^"]*)"\s*(==|!={}|!=\(\)=|={}|=\(\)=|<(?:\(\)|)=|<(?:\(\)|)<|>(?:\(\)|)=|>(?:\(\)|)>|!=)\s*(.+)$/;
  const match = trimmed.match(regex);

  if (!match) {
    return null;
  }

  const targetPrefix = match[1];
  const fieldId = match[2];
  const targetType = targetPrefix === 'ai' ? 'additional_info' : 'field';
  let rawOperator = match[3];
  const rawValue = match[4].trim();

  // Normalize operator format
  let operator: OperatorType = '==';
  if (rawOperator === '==') operator = '==';
  else if (rawOperator === '!=') operator = '!=';
  else if (rawOperator === '=()=' || rawOperator === '={}') operator = '=()=';
  else if (rawOperator === '!=()=' || rawOperator === '!={}') operator = '!=()=';
  else if (rawOperator === '<()=' || rawOperator === '<=') operator = '<()=';
  else if (rawOperator === '<()<' || rawOperator === '<') operator = '<()<';
  else if (rawOperator === '>()=' || rawOperator === '>=') operator = '>()=';
  else if (rawOperator === '>()>' || rawOperator === '>') operator = '>()>';

  let valueType: ValueType = 'string';
  const cleanVal = cleanValueString(rawValue);

  if (rawValue.startsWith('{') && rawValue.endsWith('}')) {
    const inner = rawValue.slice(1, -1).trim();
    if (!isNaN(Number(inner)) && inner !== '') {
      valueType = 'count';
    } else if (operator === '=()=' || operator === '!=()=') {
      valueType = 'selection';
    } else {
      valueType = 'string';
    }
  } else if (['<()=', '<()<', '>()=', '>()>'].includes(operator)) {
    valueType = 'number';
  } else if (operator === '=()=' || operator === '!=()=') {
    valueType = 'selection';
  } else {
    valueType = 'string';
  }

  return {
    id: generateId(),
    fieldId,
    targetType,
    operator,
    valueType,
    value: cleanVal,
  };
}

/**
 * Parse full condition string that may have && or ||
 * e.g., f."f_status" == "Ya" && f."f_umur" >()= "18"
 */
export function parseConditions(condExpr: string): ConditionItem[] {
  const items: ConditionItem[] = [];
  if (!condExpr.trim()) return items;

  // Split by && or || keeping delimiters
  const tokens = condExpr.split(/(&&|\|\|)/);
  let currentLogic: '&&' | '||' | undefined = undefined;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i].trim();
    if (token === '&&' || token === '||') {
      currentLogic = token;
    } else if (token) {
      const condItem = parseSingleCondition(token);
      if (condItem) {
        if (items.length > 0 && currentLogic) {
          items[items.length - 1].logicOperator = currentLogic;
        }
        items.push(condItem);
      }
    }
  }

  return items;
}

/**
 * Parse an Action expression string
 */
export function parseAction(actionExpr: string): ActionItem {
  const trimmed = actionExpr.trim();

  // open_page("page_id")
  let match = trimmed.match(/^open_page\(\s*"([^"]*)"\s*\)$/);
  if (match) {
    return { type: 'open_page', pageId: match[1] };
  }

  // submit
  if (trimmed === 'submit') {
    return { type: 'submit' };
  }

  // end("pesan")
  match = trimmed.match(/^end\(\s*"([^"]*)"\s*\)$/);
  if (match) {
    return { type: 'end', infoMessage: match[1] };
  }

  // cond_show("field_id")
  match = trimmed.match(/^cond_show\(\s*"([^"]*)"\s*\)$/);
  if (match) {
    return { type: 'cond_show', targetFieldId: match[1] };
  }

  // hide("field_id")
  match = trimmed.match(/^hide\(\s*"([^"]*)"\s*\)$/);
  if (match) {
    return { type: 'hide', targetFieldId: match[1] };
  }

  // field_option_nothing(f."field_id", "option")
  match = trimmed.match(/^field_option_nothing\(\s*f\."([^"]*)"\s*,\s*"([^"]*)"\s*\)$/);
  if (match) {
    return { type: 'field_option_nothing', targetFieldId: match[1], exclusiveOption: match[2] };
  }

  // field_min_value_by_reference("target_field_id", "ref_field_id")
  match = trimmed.match(/^field_min_value_by_reference\(\s*"([^"]*)"\s*,\s*"([^"]*)"\s*\)$/);
  if (match) {
    return { type: 'field_min_value_by_reference', targetFieldId: match[1], refFieldId: match[2] };
  }

  // matrix_target("target_field_id")
  match = trimmed.match(/^matrix_target\(\s*"([^"]*)"\s*\)$/);
  if (match) {
    return { type: 'matrix_target', targetFieldId: match[1] };
  }

  // option_reference_by_info(f."field_id", "info_key")
  match = trimmed.match(/^option_reference_by_info\(\s*f\."([^"]*)"\s*,\s*"([^"]*)"\s*\)$/);
  if (match) {
    return { type: 'option_reference_by_info', targetFieldId: match[1], infoKey: match[2] };
  }

  // Legacy format (no target field): option_reference_by_info("info_key")
  match = trimmed.match(/^option_reference_by_info\(\s*"([^"]*)"\s*\)$/);
  if (match) {
    return { type: 'option_reference_by_info', infoKey: match[1] };
  }

  return { type: 'custom', rawText: trimmed };
}

/**
 * Parse a single statement string (which might be conditional or unconditional)
 */
export function parseStatement(stmtStr: string): RuleStatement {
  const trimmed = stmtStr.trim();
  const qIndex = trimmed.indexOf('?');

  if (qIndex !== -1) {
    const condPart = trimmed.substring(0, qIndex).trim();
    const actionPart = trimmed.substring(qIndex + 1).trim();

    const conditions = parseConditions(condPart);
    const action = parseAction(actionPart);

    if (conditions.length > 0) {
      return {
        id: generateId(),
        isConditional: true,
        conditions,
        action,
      };
    }
  }

  // Unconditional action or fallback
  const action = parseAction(trimmed);
  return {
    id: generateId(),
    isConditional: false,
    conditions: [],
    action,
    rawStatement: action.type === 'custom' ? trimmed : undefined,
  };
}

/**
 * Parse entire Action Rules string
 */
export function parseActionRules(rawRulesStr: string): ActionRuleModel {
  const result: ActionRuleModel = {
    preRules: [],
    afterRules: [],
  };

  if (!rawRulesStr || !rawRulesStr.trim()) {
    return result;
  }

  const trimmed = rawRulesStr.trim();
  let prePart = '';
  let afterPart = '';

  if (trimmed.includes('##')) {
    const parts = trimmed.split('##');
    prePart = parts[0] || '';
    afterPart = parts[1] || '';
  } else {
    afterPart = trimmed;
  }

  if (prePart.trim()) {
    const stmts = prePart.split(';').map((s) => s.trim()).filter(Boolean);
    result.preRules = stmts.map(parseStatement);
  }

  if (afterPart.trim()) {
    const stmts = afterPart.split(';').map((s) => s.trim()).filter(Boolean);
    result.afterRules = stmts.map(parseStatement);
  }

  return result;
}

/**
 * Generate string from ConditionItem
 */
export function generateConditionStr(cond: ConditionItem): string {
  const prefix = cond.targetType === 'additional_info' ? 'ai' : 'f';
  const fieldRef = `${prefix}."${cond.fieldId}"`;
  const cleanVal = cleanValueString(cond.value);

  let valStr = `"${cleanVal}"`;
  if (cond.valueType === 'count') {
    valStr = `{${cleanVal}}`;
  } else if (cond.operator === '=()=' || cond.operator === '!=()=') {
    valStr = `{"${cleanVal}"}`;
  } else {
    valStr = `"${cleanVal}"`;
  }

  return `${fieldRef} ${cond.operator} ${valStr}`;
}

/**
 * Generate string from ActionItem
 */
export function generateActionStr(action: ActionItem): string {
  switch (action.type) {
    case 'open_page':
      return `open_page("${action.pageId || ''}")`;
    case 'submit':
      return `submit`;
    case 'end':
      return `end("${action.infoMessage || ''}")`;
    case 'cond_show':
      return `cond_show("${action.targetFieldId || ''}")`;
    case 'hide':
      return `hide("${action.targetFieldId || ''}")`;
    case 'field_option_nothing':
      return `field_option_nothing(f."${action.targetFieldId || ''}", "${action.exclusiveOption || ''}")`;
    case 'field_min_value_by_reference':
      return `field_min_value_by_reference("${action.targetFieldId || ''}", "${action.refFieldId || ''}")`;
    case 'matrix_target':
      return `matrix_target("${action.targetFieldId || ''}")`;
    case 'option_reference_by_info':
      return `option_reference_by_info(f."${action.targetFieldId || ''}", "${action.infoKey || ''}")`;
    case 'custom':
    default:
      return action.rawText || '';
  }
}

/**
 * Generate string from RuleStatement
 */
export function generateStatementStr(stmt: RuleStatement): string {
  if (stmt.rawStatement && stmt.action.type === 'custom') {
    return stmt.rawStatement;
  }

  const actionStr = generateActionStr(stmt.action);
  if (!stmt.isConditional || stmt.conditions.length === 0) {
    return actionStr;
  }

  const condStrList: string[] = [];
  stmt.conditions.forEach((cond, idx) => {
    let s = generateConditionStr(cond);
    if (idx < stmt.conditions.length - 1 && cond.logicOperator) {
      s += ` ${cond.logicOperator}`;
    }
    condStrList.push(s);
  });

  return `${condStrList.join(' ')} ? ${actionStr}`;
}

/**
 * Generate complete Action Rules string from ActionRuleModel
 */
export function generateActionRules(model: ActionRuleModel): string {
  const preStrList = model.preRules.map(generateStatementStr).filter(Boolean);
  const afterStrList = model.afterRules.map(generateStatementStr).filter(Boolean);

  const preStr = preStrList.join(' ; ');
  const afterStr = afterStrList.join(' ; ');

  if (preStr && afterStr) {
    return `${preStr} ## ${afterStr}`;
  } else if (preStr) {
    return `${preStr} ##`;
  } else {
    return afterStr;
  }
}
